
import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { exportElementByIdToPdf } from '@/utils/exportToPdf';
import { PostActionModal } from './PostActionModal';

interface ToolPdfExportButtonProps {
  resultId: string;
  fileName?: string;
  toolName: string;
  title?: string;
  orientation?: 'portrait' | 'landscape';
  className?: string;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
}

export const ToolPdfExportButton = ({
  resultId,
  fileName = 'export-alunatools',
  toolName,
  title = 'Exporter en PDF',
  orientation = 'portrait',
  className,
  size = 'sm',
  variant = 'outline',
}: ToolPdfExportButtonProps) => {
  const { user } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [showPostActionModal, setShowPostActionModal] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    const result = await exportElementByIdToPdf(resultId, {
      fileName,
      sourceTool: toolName,
      orientation,
    });

    if (!result.ok) {
      toast.error(result.error ?? 'Impossible de générer le PDF');
      setIsExporting(false);
      return;
    }

    if (!user) {
      setShowPostActionModal(true);
    }

    toast.success(
      result.error
        ? `PDF téléchargé. ${result.error}`
        : 'PDF téléchargé et enregistré dans Mes Exports'
    );
    setIsExporting(false);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleExport}
        disabled={isExporting}
        className={className}
      >
        <FileText className="h-4 w-4" />
        <span className="ml-2">{isExporting ? 'Export PDF...' : title}</span>
      </Button>

      <PostActionModal
        open={showPostActionModal}
        onClose={() => setShowPostActionModal(false)}
        toolSlug={toolName}
      />
    </>
  );
};
